import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import InputField from 'components/InputField';
import EmailOrPhoneFieldWrapper from 'components/EmailOrPhoneFieldWrapper';
import ReCAPTCHA from 'react-google-recaptcha';
import env from 'env';
import { Button, Loader } from 'ncoded-component-library';
import useValidations from '../../hooks/useValidations';
import AlternateLogins from '../../components/AlternateLogins';
import { Link } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import PasswordField from 'components/PasswordField';
import classNames from 'classnames';
import AuthContext from 'providers/Auth/Auth.context';
import utils, {
  capitalizeName,
  getEmailOrPhoneForLoginOrRegister,
  getResolveUrl,
  getSellingPortfolio,
} from 'utils';
import HelmetWrapper from 'components/HelmetWrapper';
import { LOGIN_ROUTE, PROFILE_ROUTE, SIGNUP_ROUTE } from 'data/global';
import useMatchMedia from 'hooks/useMatchMedia';
import { RegisterForm } from 'models/AuthBody';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  SIGNUP_METHOD,
  SIGNUP_SOURCE,
  SignupInitiatedProps,
} from 'models/MixpanelEvents';
import credentialsService from 'services/credentialsService';

import './SignUp.styles.scss';
import './SignUp.styles.responsive.scss';

type SignUpProps = {
  className?: string;
};

const SignUp: React.FC<SignUpProps> = () => {
  const { t } = useTranslation();
  const { popServerError, popError } = useContext(PopNotificationsContext);
  const recaptchaRef = useRef(null);
  const {
    params: { token, dealRoomId, listingEnquiryId },
  } = useQueryParams();
  const [isLoadingVals, setIsLoadingVals] = useState(false);
  const [defaultVals, setDefaultVals] = useState(null);
  const { onLoginReq } = useContext(AuthContext);

  const isPhablet = useMatchMedia('isPhablet');
  const commonValidations = useValidations();
  const classes = classNames('sign-up', { 'with-token': !!token });

  const getMixpanelProps = useGetMixpanelProps();

  const signupSource = useMemo(() => {
    if (!token) return SIGNUP_SOURCE.ORGANIC;

    if (dealRoomId) return SIGNUP_SOURCE.BROKER_INVITATION;

    if (listingEnquiryId) return SIGNUP_SOURCE.LEAD_INVITATION;

    return SIGNUP_SOURCE.ORGANIC;
  }, [dealRoomId, listingEnquiryId, token]);

  const onSubmit = useCallback(
    async (values: RegisterForm) => {
      const recaptchaResponseToken = recaptchaRef.current?.getValue();

      if (!recaptchaResponseToken) {
        popError(t('recaptchaRequired'));
        return;
      }

      const { email, emailOrPhone, ...rest } = values;

      const signupMethod = getEmailOrPhoneForLoginOrRegister({
        email,
        emailOrPhone,
      });

      const requestData = {
        recaptchaResponseToken,
        ...rest,
        ...signupMethod,
      };

      try {
        mixpanelService.signupInitiated(
          getMixpanelProps({
            socialSignup: false,
            signupSource,
            signupMethod: signupMethod.contactNumber
              ? SIGNUP_METHOD.PHONE_NUMBER
              : SIGNUP_METHOD.EMAIL,
          }) as SignupInitiatedProps,
        );

        const { data: authBody } = await api.auth.register(requestData);

        // used for phone number verification reminder
        credentialsService.hasJustRegistered = 'true';

        onLoginReq(
          authBody,
          dealRoomId ? getSellingPortfolio(dealRoomId) : PROFILE_ROUTE,
        );
      } catch (e) {
        popServerError(e);
        recaptchaRef.current?.reset();
      }
    },
    [
      dealRoomId,
      signupSource,
      popError,
      t,
      getMixpanelProps,
      onLoginReq,
      popServerError,
    ],
  );

  useEffect(() => {
    if (token) {
      setIsLoadingVals(true);
      (async () => {
        try {
          const { data } = await api.auth.getPrefilingValues(token);
          setDefaultVals(data);
        } catch (e) {
          popServerError(e);
        } finally {
          setIsLoadingVals(false);
        }
      })();
    }
  }, [popServerError, token]);

  const preFormContent = useMemo(
    () =>
      token ? (
        <div className="signup-heading-container">
          <h1>{t('letsFinish')}</h1>
          <p>{t('beSureToCheckInfo')}</p>
        </div>
      ) : (
        <h1>{t('createAnAccount')}</h1>
      ),
    [t, token],
  );

  const initialValues = useMemo(() => {
    if (!defaultVals) return {};

    const { email, contactNumber, ...rest } = defaultVals;

    return { ...rest, emailOrPhone: email || contactNumber || '' };
  }, [defaultVals]);

  useEffect(() => {
    if (token) {
      setIsLoadingVals(true);
      (async () => {
        try {
          const { data } = await api.auth.getPrefilingValues(token);
          setDefaultVals(data);
        } catch (e) {
          popServerError(e);
        } finally {
          setIsLoadingVals(false);
        }
      })();
    }
  }, [popServerError, token]);

  const helmetUrl = getResolveUrl(SIGNUP_ROUTE);
  const title = 'Create an account with Resolve';
  const description =
    'Create an account with Resolve to start buying or selling businesses today.';

  return (
    <>
      <HelmetWrapper title={title} description={description} url={helmetUrl} />
      {preFormContent}
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, valid }) => (
          <form className={classes} onSubmit={handleSubmit}>
            <Field
              name="firstName"
              required
              placeholder={t('firstName')}
              label={t('firstName')}
              component={InputField}
              validate={commonValidations.name}
              formatOnBlur
              format={capitalizeName}
            />
            <Field
              name="lastName"
              required
              placeholder={t('lastName')}
              label={t('lastName')}
              component={InputField}
              validate={commonValidations.name}
              formatOnBlur
              format={capitalizeName}
            />

            <EmailOrPhoneFieldWrapper readOnly={!!token} />

            <Field
              name="password"
              required
              placeholder={t('password')}
              label={t('password')}
              component={PasswordField}
              validate={commonValidations.password}
            />

            {!token ? (
              <p className="create-acc">
                {t('alreadyHaveAccount')}{' '}
                <Link to={LOGIN_ROUTE}>{t('signIn')}</Link>
              </p>
            ) : (
              <span />
            )}

            {!utils.isSSG() ? (
              <ReCAPTCHA
                className="recaptcha"
                ref={recaptchaRef}
                sitekey={env.ReCAPTCHA_KEY}
              />
            ) : (
              <span />
            )}

            {!isPhablet && <span />}
            <div className="actions">
              <Button
                type="submit"
                disabled={!valid || submitting}
                data-loading={submitting}
              >
                {t(token ? 'completeYourAccount' : 'createAccount')}
              </Button>
            </div>
          </form>
        )}
      />
      <AlternateLogins signupSource={signupSource} />
      {isLoadingVals && <Loader />}
    </>
  );
};

export default SignUp;
